import { addDays } from 'date-fns/addDays';
import { addMinutes } from 'date-fns/addMinutes';
import { differenceInCalendarDays } from 'date-fns/differenceInCalendarDays';
import { isAfter } from 'date-fns/isAfter';
import { isBefore } from 'date-fns/isBefore';

import { dataViewI18nT } from '../../../../core/i18n.js';
import {
  appendRecurrenceException,
  type CalendarOccurrence,
} from '../../calendar-domain.js';
import type { CalendarSingleView } from '../../calendar-view-manager.js';
import type { CalendarEvent } from '../calendar-view-types.js';
import {
  copyCalendarOccurrence,
  cutCalendarOccurrence,
  pasteCalendarOccurrence,
} from './calendar-event-clipboard.js';

const CALENDAR_KEYBOARD_STEP_MINUTES = 15;
const CALENDAR_POINTER_STEP_PIXELS = 12;

type SetCellJson = (
  rowId: string,
  propertyId: string | undefined,
  value: unknown
) => void;

export type CalendarEventInteractionContext = {
  addRow: () => string | undefined;
  clearCalendarEventEditPreview: () => void;
  commitCalendarEventEdit: (
    event: CalendarOccurrence<CalendarEvent>,
    startAt: Date,
    endAt: Date,
    options?: { allDay?: boolean }
  ) => void;
  deleteEvent: (rowId: string) => void;
  previewCalendarEventEdit: (
    event: CalendarOccurrence<CalendarEvent>,
    startAt: Date,
    endAt: Date,
    options?: { allDay?: boolean }
  ) => void;
  readonly: () => boolean;
  setCalendarError: (message: string | undefined) => void;
  setCellJson: SetCellJson;
  setSelectedOccurrence: (
    rowId: string | undefined,
    occurrenceStart: string | undefined
  ) => void;
  view: CalendarSingleView;
};

function canMutateOccurrence(
  context: CalendarEventInteractionContext,
  event: CalendarOccurrence<CalendarEvent>
): boolean {
  return !context.readonly() && !event.source.externalReadonly;
}

export function shiftCalendarOccurrenceDays(
  context: CalendarEventInteractionContext,
  event: CalendarOccurrence<CalendarEvent>,
  days: number
): void {
  if (!canMutateOccurrence(context, event)) return;
  const source = event.source;
  context.setCellJson(
    source.rowId,
    context.view.startDateCol$.value,
    addDays(source.startAt, days).getTime()
  );
  context.setCellJson(
    source.rowId,
    context.view.endDateCol$.value,
    addDays(source.endAt, days).getTime()
  );
}

export function shiftCalendarOccurrenceMinutes(
  context: CalendarEventInteractionContext,
  event: CalendarOccurrence<CalendarEvent>,
  minutes: number
): void {
  if (!canMutateOccurrence(context, event)) return;
  const source = event.source;
  context.setCellJson(
    source.rowId,
    context.view.startDateCol$.value,
    addMinutes(source.startAt, minutes).getTime()
  );
  context.setCellJson(
    source.rowId,
    context.view.endDateCol$.value,
    addMinutes(source.endAt, minutes).getTime()
  );
}

export function resizeCalendarOccurrenceEndDays(
  context: CalendarEventInteractionContext,
  event: CalendarOccurrence<CalendarEvent>,
  days: number
): void {
  if (!canMutateOccurrence(context, event)) return;
  const source = event.source;
  const nextEnd = addDays(source.endAt, days);
  const minimumEnd = source.allDay
    ? source.startAt
    : addMinutes(source.startAt, CALENDAR_KEYBOARD_STEP_MINUTES);
  if (isBefore(nextEnd, minimumEnd)) return;
  context.setCellJson(
    source.rowId,
    context.view.endDateCol$.value,
    nextEnd.getTime()
  );
}

export function resizeCalendarOccurrenceEndMinutes(
  context: CalendarEventInteractionContext,
  event: CalendarOccurrence<CalendarEvent>,
  minutes: number
): void {
  if (!canMutateOccurrence(context, event)) return;
  const source = event.source;
  const nextEnd = addMinutes(source.endAt, minutes);
  if (
    isBefore(
      nextEnd,
      addMinutes(source.startAt, CALENDAR_KEYBOARD_STEP_MINUTES)
    )
  ) {
    return;
  }
  context.setCellJson(
    source.rowId,
    context.view.endDateCol$.value,
    nextEnd.getTime()
  );
}

export function moveCalendarOccurrenceToDay(
  context: CalendarEventInteractionContext,
  event: CalendarOccurrence<CalendarEvent>,
  day: Date
): void {
  if (!canMutateOccurrence(context, event)) return;
  const source = event.source;
  const delta = differenceInCalendarDays(day, event.startAt);
  context.setCellJson(
    source.rowId,
    context.view.startDateCol$.value,
    addDays(source.startAt, delta).getTime()
  );
  context.setCellJson(
    source.rowId,
    context.view.endDateCol$.value,
    addDays(source.endAt, delta).getTime()
  );
}

export function handleCalendarEventKeyDown(
  context: CalendarEventInteractionContext,
  key: KeyboardEvent,
  event: CalendarOccurrence<CalendarEvent>
): void {
  const primaryModifier = key.metaKey || key.ctrlKey;
  if (primaryModifier && key.key.toLowerCase() === 'c') {
    key.preventDefault();
    copyCalendarOccurrence(event);
    return;
  }
  if (primaryModifier && key.key.toLowerCase() === 'x') {
    if (!canMutateOccurrence(context, event)) return;
    key.preventDefault();
    cutCalendarOccurrence(event, () => context.deleteEvent(event.source.rowId));
    return;
  }
  if (primaryModifier && key.key.toLowerCase() === 'v') {
    key.preventDefault();
    pasteCalendarOccurrence(context);
    return;
  }
  if (key.key === 'Enter' || key.key === ' ') {
    key.preventDefault();
    context.setSelectedOccurrence(
      event.source.rowId,
      event.startAt.toISOString()
    );
    return;
  }
  if (!canMutateOccurrence(context, event)) return;
  if (key.key === 'ArrowLeft' || key.key === 'ArrowRight') {
    key.preventDefault();
    const days = key.key === 'ArrowLeft' ? -1 : 1;
    if (key.shiftKey) resizeCalendarOccurrenceEndDays(context, event, days);
    else shiftCalendarOccurrenceDays(context, event, days);
  }
  if (key.key === 'ArrowUp' || key.key === 'ArrowDown') {
    key.preventDefault();
    const minutes =
      key.key === 'ArrowUp'
        ? -CALENDAR_KEYBOARD_STEP_MINUTES
        : CALENDAR_KEYBOARD_STEP_MINUTES;
    if (key.shiftKey)
      resizeCalendarOccurrenceEndMinutes(context, event, minutes);
    else shiftCalendarOccurrenceMinutes(context, event, minutes);
  }
  if (key.key === 'Delete' || key.key === 'Backspace') {
    key.preventDefault();
    if (event.isRecurring) cancelCalendarOccurrence(context, event);
    else context.deleteEvent(event.source.rowId);
  }
}

export function startCalendarOccurrenceResizePointer(
  context: CalendarEventInteractionContext,
  pointer: PointerEvent,
  event: CalendarOccurrence<CalendarEvent>,
  edge: 'end' | 'start'
): void {
  if (!canMutateOccurrence(context, event)) return;
  pointer.preventDefault();
  pointer.stopPropagation();
  const target = pointer.currentTarget as HTMLElement;
  if (typeof target.setPointerCapture === 'function') {
    target.setPointerCapture(pointer.pointerId);
  }
  const initialY = pointer.clientY;
  const initialStart = event.source.startAt;
  const initialEnd = event.source.endAt;
  let appliedMinutes = 0;
  let latestEnd = initialEnd;
  let latestStart = initialStart;
  const onMove = (move: PointerEvent) => {
    const nextMinutes =
      Math.round((move.clientY - initialY) / CALENDAR_POINTER_STEP_PIXELS) *
      CALENDAR_KEYBOARD_STEP_MINUTES;
    if (nextMinutes === appliedMinutes) return;
    appliedMinutes = nextMinutes;
    if (edge === 'start') {
      const nextStart = addMinutes(initialStart, nextMinutes);
      if (
        isAfter(
          nextStart,
          addMinutes(initialEnd, -CALENDAR_KEYBOARD_STEP_MINUTES)
        )
      ) {
        return;
      }
      latestStart = nextStart;
      latestEnd = initialEnd;
    } else {
      const nextEnd = addMinutes(initialEnd, nextMinutes);
      if (
        isBefore(
          nextEnd,
          addMinutes(initialStart, CALENDAR_KEYBOARD_STEP_MINUTES)
        )
      ) {
        return;
      }
      latestStart = initialStart;
      latestEnd = nextEnd;
    }
    context.previewCalendarEventEdit(event, latestStart, latestEnd);
  };
  const cleanup = () => {
    target.removeEventListener('pointermove', onMove);
    target.removeEventListener('pointerup', onPointerUp);
    target.removeEventListener('pointercancel', onPointerCancel);
    if (
      typeof target.hasPointerCapture === 'function' &&
      target.hasPointerCapture(pointer.pointerId) &&
      typeof target.releasePointerCapture === 'function'
    ) {
      target.releasePointerCapture(pointer.pointerId);
    }
  };
  const onPointerUp = () => {
    cleanup();
    if (
      latestStart.getTime() === initialStart.getTime() &&
      latestEnd.getTime() === initialEnd.getTime()
    ) {
      context.clearCalendarEventEditPreview();
      return;
    }
    context.commitCalendarEventEdit(event, latestStart, latestEnd);
  };
  const onPointerCancel = () => {
    cleanup();
    context.clearCalendarEventEditPreview();
  };
  target.addEventListener('pointermove', onMove);
  target.addEventListener('pointerup', onPointerUp);
  target.addEventListener('pointercancel', onPointerCancel);
}

export function cancelCalendarOccurrence(
  context: CalendarEventInteractionContext,
  event: CalendarOccurrence<CalendarEvent>
): boolean {
  if (!canMutateOccurrence(context, event)) return false;
  const view = context.view;
  if (!view.recurrenceExdateCol$.value) {
    view.ensureCalendarColumns();
  }
  const recurrenceExdateCol = view.recurrenceExdateCol$.value;
  if (!recurrenceExdateCol) {
    context.setCalendarError(
      dataViewI18nT('calendar.error.recurrenceExceptionMissing')
    );
    return false;
  }
  const source = event.source;
  context.setCellJson(
    source.rowId,
    recurrenceExdateCol,
    appendRecurrenceException(source.recurrenceExdate, event.originalStartAt)
  );
  context.setSelectedOccurrence(undefined, undefined);
  return true;
}

export function detachCalendarOccurrence(
  context: CalendarEventInteractionContext,
  event: CalendarOccurrence<CalendarEvent>
): void {
  if (!canMutateOccurrence(context, event)) return;
  const view = context.view;
  const source = event.source;
  if (!view.recurrenceIdCol$.value) {
    view.ensureCalendarColumns();
  }
  if (!cancelCalendarOccurrence(context, event)) return;
  if (!view.recurrenceIdCol$.value) {
    context.setCalendarError(
      dataViewI18nT('calendar.error.recurrenceIdMissing')
    );
    return;
  }
  const rowId = context.addRow();
  if (!rowId) return;

  context.setCellJson(
    rowId,
    view.mainProperties$.value.titleColumn,
    source.title
  );
  context.setCellJson(rowId, view.startDateCol$.value, event.startAt.getTime());
  context.setCellJson(rowId, view.endDateCol$.value, event.endAt.getTime());
  context.setCellJson(rowId, view.allDayCol$.value, source.allDay);
  context.setCellJson(rowId, view.timezoneCol$.value, source.timezone);
  context.setCellJson(rowId, view.calendarIdCol$.value, source.calendarId);
  context.setCellJson(
    rowId,
    view.locationPhysicalCol$.value,
    source.locationPhysical
  );
  context.setCellJson(
    rowId,
    view.locationVirtualCol$.value,
    source.locationVirtual
  );
  context.setCellJson(rowId, view.attendeesCol$.value, source.attendees);
  context.setCellJson(rowId, view.reminderCol$.value, source.reminder);
  context.setCellJson(rowId, view.descriptionCol$.value, source.description);
  context.setCellJson(rowId, view.statusCol$.value, source.status);
  context.setCellJson(rowId, view.visibilityCol$.value, source.visibility);
  context.setCellJson(
    rowId,
    view.recurrenceIdCol$.value,
    event.originalStartAt.toISOString()
  );
  context.setSelectedOccurrence(rowId, event.startAt.toISOString());
}
